import type { UFile, UFileStream } from '@w/utils'
import { nullTel, type Tel } from '@w/tel'
import type {
  BucketDriver,
  BucketHooks,
  BucketMethods,
  BucketOperation,
} from './types'

export interface CreateBucketOptions {
  name: string
  logging?: boolean
  driver?: BucketDriver
  hooks?: BucketHooks
}

export function createBucket(options: CreateBucketOptions) {
  return new Bucket(options)
}

export class Bucket implements BucketMethods {
  readonly name: string
  logging = false
  protected driver?: BucketDriver
  protected hooks?: BucketHooks
  protected readonly tel: Tel

  constructor(options: CreateBucketOptions, tel: Tel = nullTel) {
    this.name = options.name
    this.logging = options.logging ?? false
    this.hooks = options.hooks
    this.tel = tel
    if (options.driver) {
      this._connectDriver(options.driver)
    }
  }

  private log(msg: string): void {
    if (this.logging) console.info(msg)
  }

  _connectDriver(driver: BucketDriver): void {
    this.driver = driver
    this.driver.__bucket__ = this
  }

  runBeforeOperationHooks(operation: BucketOperation): Promise<void> {
    return Promise.resolve(this.hooks?.beforeOperation?.(operation))
  }

  withTel(tel: Tel): this {
    return new Bucket(
      {
        name: this.name,
        logging: this.logging,
        driver: this.driver,
        hooks: this.hooks,
      },
      tel,
    ) as this
  }

  put(file: UFile | UFileStream): Promise<{ size: number }> {
    const driver = this.driver
    if (!driver) {
      return Promise.reject(new Error('Bucket driver not connected'))
    }

    this.log(`put path=${file.path}`)
    return this.tel.span(
      {
        name: 'bucket.put',
        type: 'mutation',
        target: 'bucket',
        input: {
          bucket: this.name,
          path: file.path,
          size: file.size,
          type: file.type,
        },
      },
      async () => {
        await this.runBeforeOperationHooks({ kind: 'put', file })
        return driver.put(file)
      },
    )
  }

  get(path: string): Promise<UFile | null> {
    const driver = this.driver
    if (!driver) {
      throw new Error('Bucket driver not connected')
    }
    this.log(`get path=${path}`)
    return this.tel.span(
      {
        name: 'bucket.get',
        type: 'query',
        target: 'bucket',
        input: { bucket: this.name, path },
      },
      async () => {
        await this.runBeforeOperationHooks({ kind: 'get', path })
        return driver.get(path)
      },
    )
  }

  async delete(path: string | string[]): Promise<void> {
    const driver = this.driver
    if (!driver) {
      throw new Error('Bucket driver not connected')
    }

    const paths = Array.isArray(path) ? path : [path]
    this.log(`delete paths=${paths.join(',')}`)
    await this.tel.span(
      {
        name: 'bucket.delete',
        type: 'mutation',
        target: 'bucket',
        input: { bucket: this.name, paths },
      },
      async () => {
        await this.runBeforeOperationHooks({ kind: 'delete', paths })
        await driver.delete(path)
      },
    )
  }

  move(from: string, to: string): Promise<number> {
    const driver = this.driver
    if (!driver) {
      throw new Error('Bucket driver not connected')
    }
    this.log(`move from=${from} to=${to}`)
    return this.tel.span(
      {
        name: 'bucket.move',
        type: 'mutation',
        target: 'bucket',
        input: { bucket: this.name, from, to },
      },
      async () => {
        await this.runBeforeOperationHooks({ kind: 'move', from, to })
        return driver.move(from, to)
      },
    )
  }

  list(): Promise<string[]> {
    const driver = this.driver
    if (!driver) {
      throw new Error('Bucket driver not connected')
    }
    return this.tel.span(
      {
        name: 'bucket.list',
        type: 'query',
        target: 'bucket',
        input: { bucket: this.name },
      },
      async () => {
        await this.runBeforeOperationHooks({ kind: 'list' })
        return driver.list()
      },
    )
  }

  async _clear(): Promise<void> {
    const driver = this.driver
    if (!driver) {
      throw new Error('Bucket driver not connected')
    }
    await this.tel.span(
      {
        name: 'bucket.clear',
        type: 'mutation',
        target: 'bucket',
        input: { bucket: this.name },
      },
      async () => {
        await this.runBeforeOperationHooks({ kind: 'clear' })
        await driver._clear()
      },
    )
  }
}
